import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  CogIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  EnvelopeIcon,
  DocumentIcon,
  PaintBrushIcon,
  ChartBarIcon,
  BellIcon,
  LanguageIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const SuperAdminSettings = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  // Récupérer les paramètres depuis l'API
  const { data: settingsData, isLoading: settingsLoading } = useQuery(
    'system-settings',
    async () => {
      const response = await axios.get('/api/super-admin/settings');
      return response.data.data;
    }
  );

  const [settings, setSettings] = useState({
    // Configuration Générale
    general: {
      platformName: 'Bourse d\'Idées - Innovactive 360°',
      description: 'Plateforme d\'accompagnement des entrepreneurs et startups',
      logo: '',
      favicon: '',
      baseUrl: 'http://localhost:3000',
      defaultLanguage: 'fr',
      timezone: 'Europe/Paris'
    },
    // Sécurité
    security: {
      jwtSecret: 'innovactive_secret_2024',
      tokenExpiration: '7d',
      passwordMinLength: 8,
      passwordComplexity: true,
      passwordExpiration: false,
      maxLoginAttempts: 5,
      lockoutDuration: 15,
      twoFactorAuth: false
    },
    // Paiements
    payments: {
      stripeSecretKey: 'sk_test_51H1234567890abcdefghijklmnopqrstuvwxyz',
      stripePublishableKey: 'pk_test_51H1234567890abcdefghijklmnopqrstuvwxyz',
      stripeWebhookSecret: 'whsec_1234567890abcdefghijklmnopqrstuvwxyz',
      testMode: true,
      defaultCurrency: 'EUR',
      commissionRate: 5
    },
    // Email
    email: {
      smtpHost: 'smtp.gmail.com',
      smtpPort: 587,
      emailUser: 'test@innovactive.com',
      emailPassword: 'test_password',
      emailNotifications: true,
      emailTemplates: true
    },
    // Fichiers
    files: {
      maxFileSize: 10485760, // 10MB
      allowedTypes: ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx'],
      uploadPath: './uploads',
      imageCompression: true,
      cdnEnabled: false
    },
    // Interface
    interface: {
      primaryColor: '#3B82F6',
      secondaryColor: '#1E40AF',
      defaultTheme: 'light',
      customFooter: '',
      legalMentions: '',
      privacyPolicy: '',
      termsOfService: ''
    },
    // Analytics
    analytics: {
      googleAnalyticsId: '',
      facebookPixel: '',
      conversionTracking: true,
      dataRetention: 365
    },
    // Notifications
    notifications: {
      pushNotifications: true,
      emailNotifications: true,
      alertThresholds: {
        users: 1000,
        projects: 500,
        revenue: 10000
      },
      checkFrequency: 30
    },
    // International
    international: {
      supportedCountries: ['FR', 'US', 'GB', 'DE', 'IT', 'ES', 'CA', 'AU', 'JP', 'CN'],
      supportedCurrencies: ['EUR', 'USD', 'GBP', 'CHF', 'CAD', 'AUD', 'JPY', 'CNY'],
      exchangeRates: true,
      localization: true
    },
    // Technique
    technical: {
      maintenanceMode: false,
      cacheEnabled: true,
      cacheDuration: 3600,
      rateLimiting: true,
      corsEnabled: true,
      logLevel: 'info'
    },
    // Maintenance
    maintenance: {
      autoBackup: true,
      backupFrequency: 'daily',
      logCleanup: true,
      dbOptimization: true
    },
    // Mobile
    mobile: {
      pwaEnabled: true,
      pushNotifications: true,
      appStoreLinks: {
        ios: '',
        android: ''
      }
    },
    navigation: {
      // Pages principales
      home: true,
      projects: true,
      users: true,
      callForProjects: true,
      subscriptions: true,
      
      // Pages utilisateur connecté
      dashboard: true,
      myProjects: true,
      myApplications: true,
      favorites: true,
      messaging: true,
      matching: true,
      profile: true,
      
      // Pages admin
      admin: true,
      superAdmin: true,
      
      // Pages spéciales
      contacts: true,
      payment: true,
      
      // Paramètres de navigation
      showUserMenu: true,
      showAdminMenu: true,
      showSuperAdminMenu: true,
      enablePublicAccess: true
    }
  });

  const sections = [
    {
      id: 'general',
      name: 'Configuration Générale',
      icon: GlobeAltIcon,
      description: 'Paramètres de base de la plateforme'
    },
    {
      id: 'security',
      name: 'Sécurité',
      icon: ShieldCheckIcon,
      description: 'Authentification et sécurité'
    },
    {
      id: 'payments',
      name: 'Paiements',
      icon: CreditCardIcon,
      description: 'Configuration Stripe et paiements'
    },
    {
      id: 'email',
      name: 'Email',
      icon: EnvelopeIcon,
      description: 'Configuration SMTP et notifications'
    },
    {
      id: 'files',
      name: 'Fichiers',
      icon: DocumentIcon,
      description: 'Gestion des uploads et stockage'
    },
    {
      id: 'interface',
      name: 'Interface',
      icon: PaintBrushIcon,
      description: 'Personnalisation de l\'apparence'
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: ChartBarIcon,
      description: 'Suivi et statistiques'
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: BellIcon,
      description: 'Alertes et notifications'
    },
    {
      id: 'international',
      name: 'International',
      icon: LanguageIcon,
      description: 'Pays, devises et localisation'
    },
    {
      id: 'technical',
      name: 'Technique',
      icon: WrenchScrewdriverIcon,
      description: 'Paramètres techniques avancés'
    },
    {
      id: 'maintenance',
      name: 'Maintenance',
      icon: ArrowPathIcon,
      description: 'Sauvegarde et maintenance'
    },
    {
      id: 'mobile',
      name: 'Mobile',
      icon: DevicePhoneMobileIcon,
      description: 'Configuration mobile et PWA'
    },
    {
      id: 'navigation',
      name: 'Navigation',
      icon: CogIcon,
      description: 'Gestion de la visibilité des pages'
    }
  ];

  const handleSettingChange = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  // Mettre à jour les paramètres quand les données arrivent de l'API (deep-merge)
  useEffect(() => {
    if (settingsData) {
      setSettings(prev => {
        const merged = { ...prev };
        for (const [key, val] of Object.entries(settingsData)) {
          merged[key] = { ...(prev[key] || {}), ...val };
        }
        return merged;
      });
    }
  }, [settingsData]);

  // Mutation pour sauvegarder les paramètres
  const saveSettingsMutation = useMutation(
    async ({ section, settings: sectionSettings }) => {
      const response = await axios.put('/api/super-admin/settings', {
        section,
        settings: sectionSettings
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message);
        setHasChanges(false);
        queryClient.invalidateQueries('system-settings');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
      }
    }
  );

  // Mutations pour les tests
  const testEmailMutation = useMutation(
    async (emailSettings) => {
      const response = await axios.post('/api/super-admin/test/email', emailSettings);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du test email');
      }
    }
  );

  const testStripeMutation = useMutation(
    async (stripeSettings) => {
      const response = await axios.post('/api/super-admin/test/stripe', stripeSettings);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du test Stripe');
      }
    }
  );

  const testWebhookMutation = useMutation(
    async (webhookSettings) => {
      const response = await axios.post('/api/super-admin/test/webhook', webhookSettings);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du test webhook');
      }
    }
  );

  const testAnalyticsMutation = useMutation(
    async (analyticsSettings) => {
      const response = await axios.post('/api/super-admin/test/analytics', analyticsSettings);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du test Analytics');
      }
    }
  );

  const testPixelMutation = useMutation(
    async (pixelSettings) => {
      const response = await axios.post('/api/super-admin/test/pixel', pixelSettings);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du test Pixel');
      }
    }
  );

  const backupMutation = useMutation(
    async () => {
      const response = await axios.post('/api/super-admin/backup');
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
      }
    }
  );

  const cleanupMutation = useMutation(
    async () => {
      const response = await axios.post('/api/super-admin/cleanup');
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du nettoyage');
      }
    }
  );

  const handleSave = async () => {
    if (!hasChanges) return;
    
    setSaving(true);
    try {
      await saveSettingsMutation.mutateAsync({
        section: activeSection,
        settings: settings[activeSection]
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (type) => {
    switch (type) {
      case 'email':
        await testEmailMutation.mutateAsync(settings.email);
        break;
      case 'paiement':
        await testStripeMutation.mutateAsync(settings.payments);
        break;
      case 'webhook':
        await testWebhookMutation.mutateAsync({ stripeWebhookSecret: settings.payments.stripeWebhookSecret });
        break;
      case 'analytics':
        await testAnalyticsMutation.mutateAsync({ googleAnalyticsId: settings.analytics.googleAnalyticsId });
        break;
      case 'pixel':
        await testPixelMutation.mutateAsync({ facebookPixel: settings.analytics.facebookPixel });
        break;
      case 'backup':
        await backupMutation.mutateAsync();
        break;
      case 'cleanup':
        await cleanupMutation.mutateAsync();
        break;
      default:
        console.log(`Test ${type} en cours...`);
    }
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nom de la plateforme
          </label>
          <input
            type="text"
            value={settings.general.platformName}
            onChange={(e) => handleSettingChange('general', 'platformName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            URL de base
          </label>
          <input
            type="url"
            value={settings.general.baseUrl}
            onChange={(e) => handleSettingChange('general', 'baseUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description de la plateforme
        </label>
        <textarea
          value={settings.general.description}
          onChange={(e) => handleSettingChange('general', 'description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Langue par défaut
          </label>
          <select
            value={settings.general.defaultLanguage}
            onChange={(e) => handleSettingChange('general', 'defaultLanguage', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="de">Deutsch</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fuseau horaire
          </label>
          <select
            value={settings.general.timezone}
            onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="Europe/Paris">Europe/Paris</option>
            <option value="Europe/London">Europe/London</option>
            <option value="America/New_York">America/New_York</option>
            <option value="America/Los_Angeles">America/Los_Angeles</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo de la plateforme
          </label>
          <input
            type="file"
            accept="image/*"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">Attention</h3>
            <p className="text-sm text-yellow-700 mt-1">
              Les modifications de sécurité peuvent affecter tous les utilisateurs connectés.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Durée d'expiration des tokens
          </label>
          <select
            value={settings.security.tokenExpiration}
            onChange={(e) => handleSettingChange('security', 'tokenExpiration', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="1h">1 heure</option>
            <option value="1d">1 jour</option>
            <option value="7d">7 jours</option>
            <option value="30d">30 jours</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Longueur minimale des mots de passe
          </label>
          <input
            type="number"
            min="6"
            max="20"
            value={settings.security.passwordMinLength}
            onChange={(e) => handleSettingChange('security', 'passwordMinLength', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tentatives de connexion max
          </label>
          <input
            type="number"
            min="3"
            max="10"
            value={settings.security.maxLoginAttempts}
            onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Durée de blocage (minutes)
          </label>
          <input
            type="number"
            min="5"
            max="60"
            value={settings.security.lockoutDuration}
            onChange={(e) => handleSettingChange('security', 'lockoutDuration', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="passwordComplexity"
            checked={settings.security.passwordComplexity}
            onChange={(e) => handleSettingChange('security', 'passwordComplexity', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="passwordComplexity" className="ml-2 block text-sm text-gray-900">
            Exiger une complexité des mots de passe (majuscules, chiffres, symboles)
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="passwordExpiration"
            checked={settings.security.passwordExpiration}
            onChange={(e) => handleSettingChange('security', 'passwordExpiration', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="passwordExpiration" className="ml-2 block text-sm text-gray-900">
            Expiration des mots de passe (90 jours)
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="twoFactorAuth"
            checked={settings.security.twoFactorAuth}
            onChange={(e) => handleSettingChange('security', 'twoFactorAuth', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="twoFactorAuth" className="ml-2 block text-sm text-gray-900">
            Authentification à deux facteurs (2FA)
          </label>
        </div>
      </div>
    </div>
  );

  const renderPaymentSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">Configuration Stripe</h3>
            <p className="text-sm text-blue-700 mt-1">
              Configurez vos clés Stripe pour activer les paiements en ligne.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Clé secrète Stripe
          </label>
          <input
            type="password"
            value={settings.payments.stripeSecretKey}
            onChange={(e) => handleSettingChange('payments', 'stripeSecretKey', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="sk_test_..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Clé publique Stripe
          </label>
          <input
            type="text"
            value={settings.payments.stripePublishableKey}
            onChange={(e) => handleSettingChange('payments', 'stripePublishableKey', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="pk_test_..."
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Webhook Secret Stripe
        </label>
        <input
          type="password"
          value={settings.payments.stripeWebhookSecret}
          onChange={(e) => handleSettingChange('payments', 'stripeWebhookSecret', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="whsec_..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Devise par défaut
          </label>
          <select
            value={settings.payments.defaultCurrency}
            onChange={(e) => handleSettingChange('payments', 'defaultCurrency', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="EUR">EUR (€)</option>
            <option value="USD">USD ($)</option>
            <option value="GBP">GBP (£)</option>
            <option value="CHF">CHF</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Taux de commission (%)
          </label>
          <input
            type="number"
            min="0"
            max="20"
            step="0.1"
            value={settings.payments.commissionRate}
            onChange={(e) => handleSettingChange('payments', 'commissionRate', parseFloat(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="testMode"
            checked={settings.payments.testMode}
            onChange={(e) => handleSettingChange('payments', 'testMode', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="testMode" className="ml-2 block text-sm text-gray-900">
            Mode test
          </label>
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={() => handleTest('paiement')}
          disabled={testStripeMutation.isLoading}
          className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${
            testStripeMutation.isLoading
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
          }`}
        >
          {testStripeMutation.isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Test en cours...
            </div>
          ) : (
            'Tester la connexion Stripe'
          )}
        </button>
        <button
          onClick={() => handleTest('webhook')}
          disabled={testWebhookMutation.isLoading}
          className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${
            testWebhookMutation.isLoading
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
          }`}
        >
          {testWebhookMutation.isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Test en cours...
            </div>
          ) : (
            'Tester le webhook'
          )}
        </button>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Serveur SMTP
          </label>
          <input
            type="text"
            value={settings.email.smtpHost}
            onChange={(e) => handleSettingChange('email', 'smtpHost', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="smtp.gmail.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Port SMTP
          </label>
          <input
            type="number"
            value={settings.email.smtpPort}
            onChange={(e) => handleSettingChange('email', 'smtpPort', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="587"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email d'envoi
          </label>
          <input
            type="email"
            value={settings.email.emailUser}
            onChange={(e) => handleSettingChange('email', 'emailUser', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="noreply@innovactive.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mot de passe email
          </label>
          <input
            type="password"
            value={settings.email.emailPassword}
            onChange={(e) => handleSettingChange('email', 'emailPassword', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="emailNotifications"
            checked={settings.email.emailNotifications}
            onChange={(e) => handleSettingChange('email', 'emailNotifications', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-900">
            Notifications par email activées
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="emailTemplates"
            checked={settings.email.emailTemplates}
            onChange={(e) => handleSettingChange('email', 'emailTemplates', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="emailTemplates" className="ml-2 block text-sm text-gray-900">
            Templates d'emails personnalisés
          </label>
        </div>
      </div>

      <button
        onClick={() => handleTest('email')}
        disabled={testEmailMutation.isLoading}
        className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${
          testEmailMutation.isLoading
            ? 'bg-gray-400 text-white cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
        }`}
      >
        {testEmailMutation.isLoading ? (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Test en cours...
          </div>
        ) : (
          'Tester l\'envoi d\'email'
        )}
      </button>
    </div>
  );

  const renderFileSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">Gestion des Fichiers</h3>
            <p className="text-sm text-blue-700 mt-1">
              Configurez les paramètres d'upload et de stockage des fichiers.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Taille maximale des fichiers (MB)
          </label>
          <input
            type="number"
            min="1"
            max="100"
            value={settings.files.maxFileSize / 1048576}
            onChange={(e) => handleSettingChange('files', 'maxFileSize', parseInt(e.target.value) * 1048576)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-gray-500 mt-1">
            Actuellement: {settings.files.maxFileSize / 1048576} MB
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Chemin de stockage
          </label>
          <input
            type="text"
            value={settings.files.uploadPath}
            onChange={(e) => handleSettingChange('files', 'uploadPath', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="./uploads"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Types de fichiers autorisés
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx', 'txt', 'zip', 'rar'].map((type) => (
            <label key={type} className="flex items-center">
              <input
                type="checkbox"
                checked={settings.files.allowedTypes.includes(type)}
                onChange={(e) => {
                  const newTypes = e.target.checked
                    ? [...settings.files.allowedTypes, type]
                    : settings.files.allowedTypes.filter(t => t !== type);
                  handleSettingChange('files', 'allowedTypes', newTypes);
                }}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">.{type}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="imageCompression"
            checked={settings.files.imageCompression}
            onChange={(e) => handleSettingChange('files', 'imageCompression', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="imageCompression" className="ml-2 block text-sm text-gray-900">
            Compression automatique des images
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="cdnEnabled"
            checked={settings.files.cdnEnabled}
            onChange={(e) => handleSettingChange('files', 'cdnEnabled', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="cdnEnabled" className="ml-2 block text-sm text-gray-900">
            CDN activé pour les fichiers statiques
          </label>
        </div>
      </div>

      {settings.files.cdnEnabled && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            URL du CDN
          </label>
          <input
            type="url"
            placeholder="https://cdn.innovactive.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      )}
    </div>
  );

  const renderInterfaceSettings = () => (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex">
          <PaintBrushIcon className="h-5 w-5 text-purple-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-purple-800">Personnalisation de l'Interface</h3>
            <p className="text-sm text-purple-700 mt-1">
              Personnalisez l'apparence et le contenu de votre plateforme.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Couleur principale
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={settings.interface.primaryColor}
              onChange={(e) => handleSettingChange('interface', 'primaryColor', e.target.value)}
              className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={settings.interface.primaryColor}
              onChange={(e) => handleSettingChange('interface', 'primaryColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Couleur secondaire
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={settings.interface.secondaryColor}
              onChange={(e) => handleSettingChange('interface', 'secondaryColor', e.target.value)}
              className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={settings.interface.secondaryColor}
              onChange={(e) => handleSettingChange('interface', 'secondaryColor', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Thème par défaut
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="theme"
              value="light"
              checked={settings.interface.defaultTheme === 'light'}
              onChange={(e) => handleSettingChange('interface', 'defaultTheme', e.target.value)}
              className="h-4 w-4 text-primary focus:ring-primary"
            />
            <div className="ml-3">
              <div className="text-sm font-medium text-gray-900">Clair</div>
              <div className="text-sm text-gray-500">Thème clair par défaut</div>
            </div>
          </label>
          <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="theme"
              value="dark"
              checked={settings.interface.defaultTheme === 'dark'}
              onChange={(e) => handleSettingChange('interface', 'defaultTheme', e.target.value)}
              className="h-4 w-4 text-primary focus:ring-primary"
            />
            <div className="ml-3">
              <div className="text-sm font-medium text-gray-900">Sombre</div>
              <div className="text-sm text-gray-500">Thème sombre par défaut</div>
            </div>
          </label>
          <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="radio"
              name="theme"
              value="auto"
              checked={settings.interface.defaultTheme === 'auto'}
              onChange={(e) => handleSettingChange('interface', 'defaultTheme', e.target.value)}
              className="h-4 w-4 text-primary focus:ring-primary"
            />
            <div className="ml-3">
              <div className="text-sm font-medium text-gray-900">Automatique</div>
              <div className="text-sm text-gray-500">Selon les préférences système</div>
            </div>
          </label>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Footer personnalisé
          </label>
          <textarea
            value={settings.interface.customFooter}
            onChange={(e) => handleSettingChange('interface', 'customFooter', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="© 2024 Innovactive 360°. Tous droits réservés."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mentions légales
          </label>
          <textarea
            value={settings.interface.legalMentions}
            onChange={(e) => handleSettingChange('interface', 'legalMentions', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Informations légales de la plateforme..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Politique de confidentialité
          </label>
          <textarea
            value={settings.interface.privacyPolicy}
            onChange={(e) => handleSettingChange('interface', 'privacyPolicy', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Politique de confidentialité..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Conditions générales d'utilisation
          </label>
          <textarea
            value={settings.interface.termsOfService}
            onChange={(e) => handleSettingChange('interface', 'termsOfService', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Conditions générales d'utilisation..."
          />
        </div>
      </div>
    </div>
  );

  const renderAnalyticsSettings = () => (
    <div className="space-y-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex">
          <ChartBarIcon className="h-5 w-5 text-green-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-green-800">Analytics et Suivi</h3>
            <p className="text-sm text-green-700 mt-1">
              Configurez les outils d'analyse et de suivi des performances.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Google Analytics ID
          </label>
          <input
            type="text"
            value={settings.analytics.googleAnalyticsId}
            onChange={(e) => handleSettingChange('analytics', 'googleAnalyticsId', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="G-XXXXXXXXXX"
          />
          <p className="text-xs text-gray-500 mt-1">
            Format: G-XXXXXXXXXX ou UA-XXXXXXXXX-X
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Facebook Pixel ID
          </label>
          <input
            type="text"
            value={settings.analytics.facebookPixel}
            onChange={(e) => handleSettingChange('analytics', 'facebookPixel', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="123456789012345"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rétention des données (jours)
          </label>
          <input
            type="number"
            min="30"
            max="2555"
            value={settings.analytics.dataRetention}
            onChange={(e) => handleSettingChange('analytics', 'dataRetention', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-gray-500 mt-1">
            Durée de conservation des données analytiques
          </p>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="conversionTracking"
            checked={settings.analytics.conversionTracking}
            onChange={(e) => handleSettingChange('analytics', 'conversionTracking', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="conversionTracking" className="ml-2 block text-sm text-gray-900">
            Suivi des conversions activé
          </label>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Événements trackés</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            'Inscription utilisateur',
            'Création de projet',
            'Paiement abonnement',
            'Contact investisseur',
            'Ajout favori',
            'Téléchargement document'
          ].map((event) => (
            <label key={event} className="flex items-center">
              <input
                type="checkbox"
                defaultChecked
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">{event}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={() => handleTest('analytics')}
          disabled={testAnalyticsMutation.isLoading}
          className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${
            testAnalyticsMutation.isLoading
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
          }`}
        >
          {testAnalyticsMutation.isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Test en cours...
            </div>
          ) : (
            'Tester Google Analytics'
          )}
        </button>
        <button
          onClick={() => handleTest('pixel')}
          disabled={testPixelMutation.isLoading}
          className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${
            testPixelMutation.isLoading
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
          }`}
        >
          {testPixelMutation.isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Test en cours...
            </div>
          ) : (
            'Tester Facebook Pixel'
          )}
        </button>
      </div>
    </div>
  );

  const renderNotificationsSettings = () => (
    <div className="space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex">
          <BellIcon className="h-5 w-5 text-orange-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-orange-800">Notifications et Alertes</h3>
            <p className="text-sm text-orange-700 mt-1">
              Configurez les notifications et les seuils d'alerte système.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="pushNotifications"
            checked={settings.notifications.pushNotifications}
            onChange={(e) => handleSettingChange('notifications', 'pushNotifications', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="pushNotifications" className="ml-2 block text-sm text-gray-900">
            Notifications push activées
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="emailNotifications"
            checked={settings.notifications.emailNotifications}
            onChange={(e) => handleSettingChange('notifications', 'emailNotifications', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-900">
            Notifications par email activées
          </label>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Seuils d'alerte</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seuil utilisateurs
            </label>
            <input
              type="number"
              min="100"
              value={settings.notifications.alertThresholds.users}
              onChange={(e) => handleSettingChange('notifications', 'alertThresholds', {
                ...settings.notifications.alertThresholds,
                users: parseInt(e.target.value)
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seuil projets
            </label>
            <input
              type="number"
              min="50"
              value={settings.notifications.alertThresholds.projects}
              onChange={(e) => handleSettingChange('notifications', 'alertThresholds', {
                ...settings.notifications.alertThresholds,
                projects: parseInt(e.target.value)
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seuil revenus (€)
            </label>
            <input
              type="number"
              min="1000"
              value={settings.notifications.alertThresholds.revenue}
              onChange={(e) => handleSettingChange('notifications', 'alertThresholds', {
                ...settings.notifications.alertThresholds,
                revenue: parseInt(e.target.value)
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Fréquence de vérification (minutes)
        </label>
        <select
          value={settings.notifications.checkFrequency}
          onChange={(e) => handleSettingChange('notifications', 'checkFrequency', parseInt(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="5">5 minutes</option>
          <option value="15">15 minutes</option>
          <option value="30">30 minutes</option>
          <option value="60">1 heure</option>
        </select>
      </div>
    </div>
  );

  const renderInternationalSettings = () => (
    <div className="space-y-6">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex">
          <LanguageIcon className="h-5 w-5 text-indigo-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-indigo-800">Configuration Internationale</h3>
            <p className="text-sm text-indigo-700 mt-1">
              Gérez les pays, devises et paramètres de localisation.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="localization"
            checked={settings.international.localization}
            onChange={(e) => handleSettingChange('international', 'localization', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="localization" className="ml-2 block text-sm text-gray-900">
            Localisation activée
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="exchangeRates"
            checked={settings.international.exchangeRates}
            onChange={(e) => handleSettingChange('international', 'exchangeRates', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="exchangeRates" className="ml-2 block text-sm text-gray-900">
            Mise à jour automatique des taux de change
          </label>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Pays supportés</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { code: 'FR', name: 'France', flag: '🇫🇷' },
            { code: 'US', name: 'États-Unis', flag: '🇺🇸' },
            { code: 'GB', name: 'Royaume-Uni', flag: '🇬🇧' },
            { code: 'DE', name: 'Allemagne', flag: '🇩🇪' },
            { code: 'IT', name: 'Italie', flag: '🇮🇹' },
            { code: 'ES', name: 'Espagne', flag: '🇪🇸' },
            { code: 'CA', name: 'Canada', flag: '🇨🇦' },
            { code: 'AU', name: 'Australie', flag: '🇦🇺' },
            { code: 'JP', name: 'Japon', flag: '🇯🇵' },
            { code: 'CN', name: 'Chine', flag: '🇨🇳' }
          ].map((country) => (
            <label key={country.code} className="flex items-center">
              <input
                type="checkbox"
                checked={settings.international.supportedCountries.includes(country.code)}
                onChange={(e) => {
                  const newCountries = e.target.checked
                    ? [...settings.international.supportedCountries, country.code]
                    : settings.international.supportedCountries.filter(c => c !== country.code);
                  handleSettingChange('international', 'supportedCountries', newCountries);
                }}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                {country.flag} {country.name}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Devises supportées</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { code: 'EUR', name: 'Euro', symbol: '€' },
            { code: 'USD', name: 'Dollar US', symbol: '$' },
            { code: 'GBP', name: 'Livre Sterling', symbol: '£' },
            { code: 'CHF', name: 'Franc Suisse', symbol: 'CHF' },
            { code: 'CAD', name: 'Dollar Canadien', symbol: 'C$' },
            { code: 'AUD', name: 'Dollar Australien', symbol: 'A$' },
            { code: 'JPY', name: 'Yen Japonais', symbol: '¥' },
            { code: 'CNY', name: 'Yuan Chinois', symbol: '¥' }
          ].map((currency) => (
            <label key={currency.code} className="flex items-center">
              <input
                type="checkbox"
                checked={settings.international.supportedCurrencies.includes(currency.code)}
                onChange={(e) => {
                  const newCurrencies = e.target.checked
                    ? [...settings.international.supportedCurrencies, currency.code]
                    : settings.international.supportedCurrencies.filter(c => c !== currency.code);
                  handleSettingChange('international', 'supportedCurrencies', newCurrencies);
                }}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                {currency.symbol} {currency.name}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTechnicalSettings = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex">
          <WrenchScrewdriverIcon className="h-5 w-5 text-gray-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-gray-800">Paramètres Techniques</h3>
            <p className="text-sm text-gray-700 mt-1">
              Configuration avancée du système et des performances.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="maintenanceMode"
            checked={settings.technical.maintenanceMode}
            onChange={(e) => handleSettingChange('technical', 'maintenanceMode', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="maintenanceMode" className="ml-2 block text-sm text-gray-900">
            Mode maintenance activé
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="cacheEnabled"
            checked={settings.technical.cacheEnabled}
            onChange={(e) => handleSettingChange('technical', 'cacheEnabled', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="cacheEnabled" className="ml-2 block text-sm text-gray-900">
            Cache activé
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="rateLimiting"
            checked={settings.technical.rateLimiting}
            onChange={(e) => handleSettingChange('technical', 'rateLimiting', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="rateLimiting" className="ml-2 block text-sm text-gray-900">
            Limitation de débit activée
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="corsEnabled"
            checked={settings.technical.corsEnabled}
            onChange={(e) => handleSettingChange('technical', 'corsEnabled', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="corsEnabled" className="ml-2 block text-sm text-gray-900">
            CORS activé
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Durée du cache (secondes)
          </label>
          <input
            type="number"
            min="60"
            max="86400"
            value={settings.technical.cacheDuration}
            onChange={(e) => handleSettingChange('technical', 'cacheDuration', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Niveau de logs
          </label>
          <select
            value={settings.technical.logLevel}
            onChange={(e) => handleSettingChange('technical', 'logLevel', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="error">Error</option>
            <option value="warn">Warning</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderMaintenanceSettings = () => (
    <div className="space-y-6">
      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
        <div className="flex">
          <ArrowPathIcon className="h-5 w-5 text-cyan-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-cyan-800">Maintenance et Sauvegarde</h3>
            <p className="text-sm text-cyan-700 mt-1">
              Configuration des tâches de maintenance automatique.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="autoBackup"
            checked={settings.maintenance.autoBackup}
            onChange={(e) => handleSettingChange('maintenance', 'autoBackup', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="autoBackup" className="ml-2 block text-sm text-gray-900">
            Sauvegarde automatique activée
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="logCleanup"
            checked={settings.maintenance.logCleanup}
            onChange={(e) => handleSettingChange('maintenance', 'logCleanup', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="logCleanup" className="ml-2 block text-sm text-gray-900">
            Nettoyage automatique des logs
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="dbOptimization"
            checked={settings.maintenance.dbOptimization}
            onChange={(e) => handleSettingChange('maintenance', 'dbOptimization', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="dbOptimization" className="ml-2 block text-sm text-gray-900">
            Optimisation automatique de la base de données
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Fréquence de sauvegarde
        </label>
        <select
          value={settings.maintenance.backupFrequency}
          onChange={(e) => handleSettingChange('maintenance', 'backupFrequency', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="hourly">Toutes les heures</option>
          <option value="daily">Quotidienne</option>
          <option value="weekly">Hebdomadaire</option>
          <option value="monthly">Mensuelle</option>
        </select>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-4">Dernières sauvegardes</h4>
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span>Sauvegarde complète</span>
            <span className="text-green-600">✓ Réussie</span>
            <span className="text-gray-500">Il y a 2 heures</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span>Sauvegarde incrémentale</span>
            <span className="text-green-600">✓ Réussie</span>
            <span className="text-gray-500">Il y a 30 minutes</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span>Nettoyage des logs</span>
            <span className="text-green-600">✓ Réussie</span>
            <span className="text-gray-500">Il y a 1 heure</span>
          </div>
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={() => handleTest('backup')}
          disabled={backupMutation.isLoading}
          className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${
            backupMutation.isLoading
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
          }`}
        >
          {backupMutation.isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Sauvegarde en cours...
            </div>
          ) : (
            'Créer une sauvegarde manuelle'
          )}
        </button>
        <button
          onClick={() => handleTest('cleanup')}
          disabled={cleanupMutation.isLoading}
          className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${
            cleanupMutation.isLoading
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
          }`}
        >
          {cleanupMutation.isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Nettoyage en cours...
            </div>
          ) : (
            'Nettoyer les logs'
          )}
        </button>
      </div>
    </div>
  );

  const renderMobileSettings = () => (
    <div className="space-y-6">
      <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
        <div className="flex">
          <DevicePhoneMobileIcon className="h-5 w-5 text-pink-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-pink-800">Configuration Mobile</h3>
            <p className="text-sm text-pink-700 mt-1">
              Paramètres pour les applications mobiles et PWA.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="pwaEnabled"
            checked={settings.mobile.pwaEnabled}
            onChange={(e) => handleSettingChange('mobile', 'pwaEnabled', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="pwaEnabled" className="ml-2 block text-sm text-gray-900">
            Application Web Progressive (PWA) activée
          </label>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="mobilePushNotifications"
            checked={settings.mobile.pushNotifications}
            onChange={(e) => handleSettingChange('mobile', 'pushNotifications', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="mobilePushNotifications" className="ml-2 block text-sm text-gray-900">
            Notifications push mobiles activées
          </label>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-4">Liens des stores d'applications</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App Store (iOS)
            </label>
            <input
              type="url"
              value={settings.mobile.appStoreLinks.ios}
              onChange={(e) => handleSettingChange('mobile', 'appStoreLinks', {
                ...settings.mobile.appStoreLinks,
                ios: e.target.value
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://apps.apple.com/app/innovactive"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Play Store (Android)
            </label>
            <input
              type="url"
              value={settings.mobile.appStoreLinks.android}
              onChange={(e) => handleSettingChange('mobile', 'appStoreLinks', {
                ...settings.mobile.appStoreLinks,
                android: e.target.value
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://play.google.com/store/apps/details?id=com.innovactive"
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-4">Statistiques PWA</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary">1,247</div>
            <div className="text-sm text-gray-600">Installations</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">89%</div>
            <div className="text-sm text-gray-600">Taux d'engagement</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">4.8</div>
            <div className="text-sm text-gray-600">Note moyenne</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNavigationSettings = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="text-sm font-medium text-blue-800">Gestion de la Navigation</h3>
        </div>
        <p className="text-sm text-blue-700 mt-1">
          Activez ou désactivez la visibilité des pages dans le menu principal
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Pages Publiques */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <GlobeAltIcon className="h-5 w-5 text-blue-600 mr-2" />
            Pages Publiques
          </h3>
          <div className="space-y-3">
            {[
              { key: 'home', label: 'Accueil', description: 'Page d\'accueil principale' },
              { key: 'projects', label: 'Projets', description: 'Liste des projets' },
              { key: 'users', label: 'Utilisateurs', description: 'Annuaire des utilisateurs' },
              { key: 'callForProjects', label: 'Appels à Projets', description: 'Appels à projets' },
              { key: 'subscriptions', label: 'Abonnements', description: 'Plans d\'abonnement' }
            ].map((page) => (
              <div key={page.key} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{page.label}</div>
                  <div className="text-sm text-gray-500">{page.description}</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.navigation[page.key]}
                  onChange={(e) => handleSettingChange('navigation', page.key, e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Pages Utilisateur Connecté */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <UserIcon className="h-5 w-5 text-green-600 mr-2" />
            Pages Utilisateur
          </h3>
          <div className="space-y-3">
            {[
              { key: 'dashboard', label: 'Tableau de Bord', description: 'Tableau de bord personnel' },
              { key: 'myProjects', label: 'Mes Projets', description: 'Gestion des projets' },
              { key: 'myApplications', label: 'Mes Candidatures', description: 'Candidatures aux appels' },
              { key: 'favorites', label: 'Favoris', description: 'Projets favoris' },
              { key: 'messaging', label: 'Messagerie', description: 'Messages privés' },
              { key: 'matching', label: 'Matching', description: 'Système de matching' },
              { key: 'profile', label: 'Profil', description: 'Profil utilisateur' }
            ].map((page) => (
              <div key={page.key} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{page.label}</div>
                  <div className="text-sm text-gray-500">{page.description}</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.navigation[page.key]}
                  onChange={(e) => handleSettingChange('navigation', page.key, e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Pages Admin */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <ShieldCheckIcon className="h-5 w-5 text-red-600 mr-2" />
            Pages Administration
          </h3>
          <div className="space-y-3">
            {[
              { key: 'admin', label: 'Administration', description: 'Panneau d\'administration' },
              { key: 'superAdmin', label: 'Super Admin', description: 'Panneau super administrateur' }
            ].map((page) => (
              <div key={page.key} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{page.label}</div>
                  <div className="text-sm text-gray-500">{page.description}</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.navigation[page.key]}
                  onChange={(e) => handleSettingChange('navigation', page.key, e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Pages Spéciales */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <CogIcon className="h-5 w-5 text-purple-600 mr-2" />
            Pages Spéciales
          </h3>
          <div className="space-y-3">
            {[
              { key: 'contacts', label: 'Contacts', description: 'Page de contact' },
              { key: 'payment', label: 'Paiement', description: 'Page de paiement' }
            ].map((page) => (
              <div key={page.key} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{page.label}</div>
                  <div className="text-sm text-gray-500">{page.description}</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.navigation[page.key]}
                  onChange={(e) => handleSettingChange('navigation', page.key, e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Paramètres de Navigation */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Paramètres de Navigation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Afficher le menu utilisateur
              </label>
              <input
                type="checkbox"
                checked={settings.navigation.showUserMenu}
                onChange={(e) => handleSettingChange('navigation', 'showUserMenu', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Afficher le menu admin
              </label>
              <input
                type="checkbox"
                checked={settings.navigation.showAdminMenu}
                onChange={(e) => handleSettingChange('navigation', 'showAdminMenu', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Afficher le menu super admin
              </label>
              <input
                type="checkbox"
                checked={settings.navigation.showSuperAdminMenu}
                onChange={(e) => handleSettingChange('navigation', 'showSuperAdminMenu', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                Accès public activé
              </label>
              <input
                type="checkbox"
                checked={settings.navigation.enablePublicAccess}
                onChange={(e) => handleSettingChange('navigation', 'enablePublicAccess', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="flex space-x-4">
        <button
          onClick={() => {
            const allPages = {
              home: true, projects: true, users: true, callForProjects: true, subscriptions: true,
              dashboard: true, myProjects: true, myApplications: true, favorites: true,
              messaging: true, matching: true, profile: true, admin: true, superAdmin: true,
              contacts: true, payment: true
            };
            setSettings(prev => ({
              ...prev,
              navigation: { ...prev.navigation, ...allPages }
            }));
            setHasChanges(true);
          }}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          Activer toutes les pages
        </button>
        <button
          onClick={() => {
            const allPages = {
              home: false, projects: false, users: false, callForProjects: false, subscriptions: false,
              dashboard: false, myProjects: false, myApplications: false, favorites: false,
              messaging: false, matching: false, profile: false, admin: false, superAdmin: false,
              contacts: false, payment: false
            };
            setSettings(prev => ({
              ...prev,
              navigation: { ...prev.navigation, ...allPages }
            }));
            setHasChanges(true);
          }}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          Désactiver toutes les pages
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return renderGeneralSettings();
      case 'security':
        return renderSecuritySettings();
      case 'payments':
        return renderPaymentSettings();
      case 'email':
        return renderEmailSettings();
      case 'files':
        return renderFileSettings();
      case 'interface':
        return renderInterfaceSettings();
      case 'analytics':
        return renderAnalyticsSettings();
      case 'notifications':
        return renderNotificationsSettings();
      case 'international':
        return renderInternationalSettings();
      case 'technical':
        return renderTechnicalSettings();
      case 'maintenance':
        return renderMaintenanceSettings();
      case 'mobile':
        return renderMobileSettings();
      case 'navigation':
        return renderNavigationSettings();
      default:
        return (
          <div className="text-center py-12">
            <CogIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Section en cours de développement
            </h3>
            <p className="text-gray-600">
              Cette section sera bientôt disponible.
            </p>
          </div>
        );
    }
  };

  // Affichage du chargement initial
  if (settingsLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des paramètres...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Paramètres Système</h2>
        <p className="text-gray-600">
          Configurez les paramètres de votre plateforme Innovactive 360°
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Navigation latérale */}
        <div className="lg:w-1/4">
          <nav className="space-y-1">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeSection === section.id
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center">
                    <Icon className="h-5 w-5 mr-3" />
                    <div>
                      <div>{section.name}</div>
                      <div className={`text-xs ${
                        activeSection === section.id ? 'text-primary-100' : 'text-gray-500'
                      }`}>
                        {section.description}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Contenu principal */}
        <div className="lg:w-3/4">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {sections.find(s => s.id === activeSection)?.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {sections.find(s => s.id === activeSection)?.description}
                </p>
              </div>
              {hasChanges && (
                <div className="flex items-center text-orange-600">
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                  <span className="text-sm">Modifications non sauvegardées</span>
                </div>
              )}
            </div>

            {renderContent()}

            {/* Boutons d'action */}
            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 ${
                  hasChanges && !saving
                    ? 'bg-primary text-white hover:bg-primary-dark focus:ring-primary'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {saving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sauvegarde...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Sauvegarder
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminSettings;
